import React, { useState, useEffect } from 'react';
import { 
  Box, Grid, Paper, Typography, Avatar, IconButton, Stack, LinearProgress, 
  Button, Divider, List, ListItem, ListItemText, ListItemIcon, Chip, 
  Accordion, AccordionSummary, AccordionDetails, useTheme
} from '@mui/material';
import {
  NotificationsNoneOutlined, SettingsOutlined, Search as SearchIcon, 
  AccessTime, PlayArrow, Pause, CheckCircle, RadioButtonUnchecked, 
  ExpandMore, ChevronRight, ChevronLeft, MoreVert, Computer, PeopleOutline, 
  PersonAddOutlined, AssignmentOutlined, ChatBubbleOutline,
  CalendarToday, Timer, TrendingUp, TrendingDown, Star, EmojiEvents,
  SwapHoriz, AccountBalance, Description, EventBusy, HowToReg, Warning
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { getAllDesignationAPICall } from '../../../../API/Staff/HRM/departmentAndDesignationAPI';
import { companyProfileInfoForUser } from '../../../../API/Settings/Company Settings/companySettingsAPI';
import { viewAllCurrencyUpdated } from '../../../../API/Settings/Currency/CurrencyAPI';
import { HEADERS } from '../../../../API/UrlAndPaths';

const HRMDashboard = () => {
  const theme = useTheme();
  const user = useSelector((state) => state.loginResponseSlice?.value?.data);
  const allDesignationList = useSelector((state) => state.allDesignationListSlice?.value);
  
  // Currency & Company Profile
  const companyProfileSlice = useSelector((state) => state.companyProfileSlice);
  const companyProfileData = companyProfileSlice?.userValue || companyProfileSlice?.value;
  const currencyListUpdated = useSelector((state) => state.viewUpdatedCurrency?.value);

  // State
  const [employees, setEmployees] = useState([]);
  const [currentEmpIndex, setCurrentEmpIndex] = useState(0);
  const [tasks, setTasks] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);

  const [leaves, setLeaves] = useState([]);
  const [transitions, setTransitions] = useState([]);
  const [loans, setLoans] = useState([]);
  const [expiringDocs, setExpiringDocs] = useState([]);
  const [attendance, setAttendance] = useState({ present: 0, absent: 0, late: 0 });

  const [performanceTab, setPerformanceTab] = useState(0); // 0: General, 1: Attendance/Leave

  const selectedEmployee = employees[currentEmpIndex] || {};

  // Fetch Employees & Designations
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch Employees
        const empRes = await axios.get('/employee/getAllEmployees');
        if (empRes.data) {
          setEmployees(empRes.data);
        }
        
        // Fetch Designations if not in store (or always refresh to be safe)
        getAllDesignationAPICall();
        // Fetch Company Profile & Currency
        companyProfileInfoForUser();
        viewAllCurrencyUpdated();
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);


  // Derive a display currency SYMBOL robustly
  const getCurrencySymbol = () => {
    const currencyObj =
      companyProfileData?.profile?.currency ??
      companyProfileData?.currency ??
      companyProfileData?.profile?.currencyId ??
      companyProfileData?.currencyId;

    let code = null;
    let storedSymbol = null;

    // Extract code and stored symbol
    if (currencyObj && typeof currencyObj === 'object') {
        code = currencyObj.cc || currencyObj.code || currencyObj.currency || currencyObj.name;
        storedSymbol = currencyObj.symbol;

        // Try to resolve from ID lookup if code is missing
        if (!code && currencyObj._id && Array.isArray(currencyListUpdated)) {
            const byId = currencyListUpdated.find(c => c?._id === currencyObj._id);
            if (byId) {
                code = byId.cc || byId.code || byId.name;
                storedSymbol = byId.symbol || storedSymbol;
            }
        }
    } else if (typeof currencyObj === 'string' && currencyObj) {
        // It might be a code or an ID
        code = currencyObj;
        // Try ID lookup
        if (Array.isArray(currencyListUpdated)) {
            const byId = currencyListUpdated.find(c => c?._id === currencyObj);
            if (byId) {
                code = byId.cc || byId.code || byId.name;
                storedSymbol = byId.symbol;
            }
        }
    }

    // 1. Try Intl.NumberFormat with the code
    if (code && typeof code === 'string') {
        try {
            const parts = new Intl.NumberFormat(undefined, { style: 'currency', currency: code, minimumFractionDigits: 0 }).formatToParts(0);
            const intlSymbol = parts.find(p => p.type === 'currency')?.value;
            
            // If Intl gives us a symbol different from the code (e.g. "₹" vs "INR"), use it
            // Also allow it if it's the same but we don't have a stored symbol
            if (intlSymbol && intlSymbol !== code) {
                return intlSymbol;
            }
        } catch (e) {
            // Invalid code, ignore
        }
    }

    // 2. Fallback to stored symbol if Intl failed or gave code
    if (storedSymbol) return storedSymbol;

    // 3. Fallback to code or default
    return code || '$'; 
  };



  // Fetch Data for Selected Employee
  useEffect(() => {
    if (selectedEmployee._id) {
      fetchEmployeeData(selectedEmployee._id);
    }
  }, [selectedEmployee._id]);

  const fetchEmployeeData = async (employeeId) => {
    try {
      // Fetch Tasks - Use correct endpoint matching EmployeeTasks.jsx
      const tasksRes = await axios.post(`/api/task/my-tasks?t=${Date.now()}`, { 
          employeeId: employeeId,
      });
      
      if (tasksRes.data && tasksRes.data.data) {
        setTasks(tasksRes.data.data);
      } else {
        setTasks([]);
      }

      // Fetch Assets (Devices)
      const assetsRes = await axios.post('/api/employee-asset/list', { 
        assignedTo: employeeId,
        employeeId: employeeId, 
        limit: 5 
      });
      
      if (assetsRes.data && assetsRes.data.data) {
        setAssets(assetsRes.data.data);
      } else {
        setAssets([]);
      }

      // Fetch Leaves (using newer viewLeave API)
      try {
          const leaveRes = await axios.get('/employee/viewLeave');
          
          if (leaveRes.data && Array.isArray(leaveRes.data)) {
             // Filter for current employee
             const myLeaves = leaveRes.data.filter(l => 
               l.empId === employeeId || l.empId === selectedEmployee._id
             );
             setLeaves(myLeaves);
          } else {
             setLeaves([]);
          }
      } catch (err) {
          console.error("Error fetching leaves:", err);
          setLeaves([]);
      }

      // Fetch Transitions
      try {
          const transRes = await axios.post('/api/employee-transition/list', { 
            employeeId: employeeId,
            limit: 5
          });
          if (transRes.data?.data) {
            setTransitions(transRes.data.data);
          }
      } catch (err) {
          console.error("Error fetching transitions:", err);
          setTransitions([]);
      }

      // Fetch Loans (Corrected Endpoint)
      try {
          // Fetch all loans and filter (legacy API pattern)
          const loanRes = await axios.get('employee/viewLoans', HEADERS);
          if (loanRes.data && Array.isArray(loanRes.data)) {
            // Filter for current employee and active status if possible
            const myLoans = loanRes.data.filter(l => 
                (l.empId === employeeId || l.employeeId === employeeId) && 
                l.loanStatus !== 'Rejected' && l.loanStatus !== 'Closed'
            );
            setLoans(myLoans);
          } else {
            setLoans([]);
          }
      } catch (err) {
          // console.error("Error fetching loans:", err);
          setLoans([]);
      }

      // Fetch Expiring Documents (Endpoint unavailable - mocked)
      setExpiringDocs([]); 
      /* 
      try {
          const docRes = await axios.post('/api/employee-document/expiring', { 
            employeeId: employeeId,
            daysAhead: 30
          });
          if (docRes.data?.data) {
            setExpiringDocs(docRes.data.data);
          }
      } catch (err) {
          console.error("Error fetching documents:", err);
          setExpiringDocs([]);
      }
      */

      // Fetch Attendance Summary (Corrected Endpoint)
      try {
          const now = new Date();
          // Start of current month
          const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
          // Use today as end date (not end of month) to avoid counting future days as absent
          const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          
          // Format YYYY-MM-DD manually to avoid dayjs dependency if not present
          const formatDate = (d) => {
              const year = d.getFullYear();
              const month = String(d.getMonth() + 1).padStart(2, '0');
              const day = String(d.getDate()).padStart(2, '0');
              return `${year}-${month}-${day}`;
          };

          const params = {
            fromDate: formatDate(firstDay),
            toDate: formatDate(today),
            employeeId: employeeId
          };

          const attRes = await axios.get('/payroll/attendance', { params });
          
          if (attRes.data && attRes.data.list && Array.isArray(attRes.data.list)) {
            // Calculate stats from list, only for this employee and up to today
            const todayStr = formatDate(today);
            const empRecords = attRes.data.list.filter(r => {
              const recDate = new Date(r.date).toISOString().split('T')[0];
              return recDate <= todayStr && (r.employeeId === employeeId || r.empCode === selectedEmployee.emp_id);
            });
            // Exclude Fridays (day 5) from absent count — common weekend day for Middle East restaurants
            // Only count as absent if it's a working day (not Friday) and has no attendance record
            const absentRecords = empRecords.filter(r => {
              if (r.status !== 'Absent') return false;
              const dayOfWeek = new Date(r.date).getDay(); // 0=Sun, 5=Fri, 6=Sat
              return dayOfWeek !== 5; // Exclude Fridays
            });
            setAttendance({
              present: empRecords.filter(r => r.status === 'On Time' || r.status === 'Present').length,
              absent: absentRecords.length,
              late: empRecords.filter(r => r.status === 'Late').length
            });
          } else if (attRes.data && attRes.data.stats) {
            setAttendance({
              present: attRes.data.stats.present || 0,
              absent: attRes.data.stats.absent || 0,
              late: attRes.data.stats.late || 0
            });
          } else {
             setAttendance({ present: 0, absent: 0, late: 0 });
          }
      } catch (err) {
          console.error("Error fetching attendance:", err);
          setAttendance({ present: 0, absent: 0, late: 0 });
      }

    } catch (error) {
      console.error("Error fetching employee data:", error);
      setTasks([]);
      setAssets([]);
      setLeaves([]);
      setTransitions([]);
      setLoans([]);
      setExpiringDocs([]);
    }
  };

  const handleNextEmployee = () => {
    setCurrentEmpIndex((prev) => (prev + 1) % employees.length);
  };

  const handlePrevEmployee = () => {
    setCurrentEmpIndex((prev) => (prev - 1 + employees.length) % employees.length);
  };

  const getImageUrl = (path) => {
    if (!path) return null;
    // Guard against backend appending FILEURL to undefined/null imageUrl
    if (path.endsWith('/undefined') || path.endsWith('/null') || path === 'undefined' || path === 'null') return null;
    if (path.startsWith('http')) return path;
    const baseUrl = axios.defaults.baseURL?.replace(/\/$/, '') || '';
    return `${baseUrl}${path.startsWith('/') ? path : '/' + path}`;
  };

  const getDesignationName = (emp) => {
    if (!emp) return 'Employee';
    // If designation is populated object
    if (emp.designation?.designationName) return emp.designation.designationName;
    
    // If designation is ID string lookup in list
    if (allDesignationList) {
        const id = typeof emp.designation === 'string' ? emp.designation : emp.designation?._id;
        if (id) {
            const found = allDesignationList.find(d => d._id === id);
            if (found) return found.designationName;
        }
    }
    
    return emp.role || 'Employee';
  };

  const formatLeaveType = (leave) => {
    if (!leave) return 'Leave';
    const raw = leave.leaveType;

    // If backend already populated an object
    if (raw && typeof raw === 'object') {
      return (
        raw.leaveTypeName ||
        raw.leaveName ||
        raw.name ||
        'Leave'
      );
    }

    // If we have a separate readable field
    if (leave.leaveTypeName) return leave.leaveTypeName;
    if (leave.leaveCategory) return leave.leaveCategory;

    // If it's a string but looks like an ObjectId, hide it
    if (typeof raw === 'string' && raw.length > 20) {
      return 'Leave';
    }

    return raw || 'Leave';
  };

  if (loading) return <LinearProgress />;

  return (
    <Box sx={{ p: 2, bgcolor: '#f5f5f5', height: 'calc(100vh - 80px)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
            <Typography variant="h5" fontWeight="700" color="#1a1d1f">Dashboard</Typography>
            <Typography variant="body2" color="text.secondary">Overview for {selectedEmployee.staff_name || 'Employee'}</Typography>
        </Box>
        <Stack direction="row" spacing={2}>
            {[
                { label: 'Tasks Pending', value: tasks.filter(t => t.status !== 'COMPLETED').length, color: '#2196f3' },
                { label: 'Leave Taken', value: leaves.length, color: '#ff9800' },
                { label: 'Active Loans', value: loans.length, color: '#9c27b0' },
            ].map((stat, idx) => (
                <Paper key={idx} elevation={0} sx={{ px: 2, py: 1, borderRadius: 2, bgcolor: 'white', minWidth: 100, textAlign: 'center' }}>
                    <Typography variant="h5" fontWeight="600" color={stat.color}>{stat.value}</Typography>
                    <Typography variant="caption" color="text.secondary">{stat.label}</Typography>
                </Paper>
            ))}
        </Stack>
      </Stack>

      <Grid container spacing={2} sx={{ flex: 1, overflow: 'auto', alignContent: 'flex-start', pb: 2 }}>
        {/* Row 1 - Profile Card + Stats Cards */}
        <Grid item xs={12} md={3} sx={{ display: 'flex' }}>
          <Paper elevation={0} sx={{ 
              p: 2, borderRadius: 3, 
              bgcolor: '#1a1d1f', 
              backgroundImage: getImageUrl(selectedEmployee.imageUrl) ? `url(${getImageUrl(selectedEmployee.imageUrl)})` : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              backgroundSize: 'cover', backgroundPosition: 'center',
              color: 'white', minHeight: 220, width: '100%', position: 'relative', overflow: 'hidden',
              display: 'flex', flexDirection: 'column'
          }}>
              <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.8) 100%)' }} />
              <Stack direction="row" justifyContent="space-between" sx={{ position: 'relative', zIndex: 1 }}>
                  <IconButton onClick={handlePrevEmployee} size="small" sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.2)' }}><ChevronLeft /></IconButton>
                  <Chip label={`${currentEmpIndex + 1}/${employees.length}`} size="small" sx={{ bgcolor: 'rgba(0,0,0,0.5)', color: 'white', height: 24 }} />
                  <IconButton onClick={handleNextEmployee} size="small" sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.2)' }}><ChevronRight /></IconButton>
              </Stack>
              <Box sx={{ mt: 'auto', position: 'relative', zIndex: 1 }}>
                  <Typography variant="h6" fontWeight="700">{selectedEmployee.staff_name}</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>{getDesignationName(selectedEmployee)}</Typography>
                  <Stack direction="row" spacing={2} mt={1}>
                      <Box><Typography variant="caption" sx={{ opacity: 0.7 }}>Tasks</Typography><Typography fontWeight="bold">{tasks.length}</Typography></Box>
                      <Box><Typography variant="caption" sx={{ opacity: 0.7 }}>Assets</Typography><Typography fontWeight="bold">{assets.length}</Typography></Box>
                  </Stack>
              </Box>
          </Paper>
        </Grid>

        {/* Attendance Card */}
        <Grid item xs={12} md={3} sx={{ display: 'flex' }}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 3, bgcolor: 'white', minHeight: 220, width: '100%', display: 'flex', flexDirection: 'column' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle2" fontWeight="600">Attendance (This Month)</Typography>
                  <HowToReg sx={{ color: '#4caf50' }} />
              </Stack>
              <Grid container spacing={1} sx={{ flex: 1, alignContent: 'center' }}>
                  <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: '#e8f5e9', borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                          <Typography variant="h4" fontWeight="600" color="#4caf50">{attendance.present}</Typography>
                          <Typography variant="caption" color="text.secondary">Present</Typography>
                      </Box>
                  </Grid>
                  <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: '#ffebee', borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                          <Typography variant="h4" fontWeight="600" color="#f44336">{attendance.absent}</Typography>
                          <Typography variant="caption" color="text.secondary">Absent</Typography>
                      </Box>
                  </Grid>
                  <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: '#fff3e0', borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                          <Typography variant="h4" fontWeight="600" color="#ff9800">{attendance.late}</Typography>
                          <Typography variant="caption" color="text.secondary">Late</Typography>
                      </Box>
                  </Grid>
              </Grid>
              <LinearProgress variant="determinate" value={(attendance.present / (attendance.present + attendance.absent + attendance.late || 1)) * 100} sx={{ mt: 2, height: 6, borderRadius: 3, bgcolor: '#e0e0e0', '& .MuiLinearProgress-bar': { bgcolor: '#4caf50' } }} />
          </Paper>
        </Grid>

        {/* Leave Status Card */}
        <Grid item xs={12} md={3} sx={{ display: 'flex' }}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 3, bgcolor: 'white', minHeight: 220, width: '100%', display: 'flex', flexDirection: 'column' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2" fontWeight="600">Leave Status</Typography>
                  <EventBusy sx={{ color: '#ff9800' }} />
              </Stack>
              <Box sx={{ flex: 1, overflowY: 'auto', maxHeight: 140 }}>
                  {leaves.length > 0 ? leaves.slice(0, 3).map((leave, idx) => (
                      <Stack key={idx} direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1, borderBottom: '1px solid #f0f0f0' }}>
                          <Box>
                              <Typography variant="body2" fontWeight="500">{formatLeaveType(leave)}</Typography>
                              <Typography variant="caption" color="text.secondary">{leave.DateFrom || leave.fromDate || ''} - {leave.DateTo || leave.toDate || ''}</Typography>
                          </Box>
                          <Chip label={leave.status || 'Pending'} size="small" sx={{ height: 20, fontSize: 10, bgcolor: leave.status === 'Approved' ? '#e8f5e9' : '#fff3e0', color: leave.status === 'Approved' ? '#4caf50' : '#ff9800' }} />
                      </Stack>
                  )) : (
                      <Box textAlign="center" py={3} sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Typography variant="caption" color="text.secondary">No leave records</Typography></Box>
                  )}
              </Box>
          </Paper>
        </Grid>

        {/* Loans Card */}
        <Grid item xs={12} md={3} sx={{ display: 'flex' }}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 3, bgcolor: 'white', minHeight: 220, width: '100%', display: 'flex', flexDirection: 'column' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2" fontWeight="600">Active Loans</Typography>
                  <AccountBalance sx={{ color: '#9c27b0' }} />
              </Stack>
              <Box sx={{ flex: 1, overflowY: 'auto', maxHeight: 140 }}>
                  {loans.length > 0 ? loans.slice(0, 3).map((loan, idx) => (
                      <Stack key={idx} direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1, borderBottom: '1px solid #f0f0f0' }}>
                          <Box>
                              <Typography variant="body2" fontWeight="500">{loan.loanType || 'Loan'}</Typography>
                              <Typography variant="caption" color="text.secondary">EMI: {loan.emiAmount || 0}</Typography>
                          </Box>
                          <Typography variant="body2" fontWeight="600" color="#9c27b0">{loan.remainingAmount || loan.amount || 0}</Typography>
                      </Stack>
                  )) : (
                      <Box textAlign="center" py={3} sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Typography variant="caption" color="text.secondary">No active loans</Typography></Box>
                  )}
              </Box>
          </Paper>
        </Grid>

        {/* Row 2 - Transitions + Documents + Assets */}
        {/* Employee Transitions */}
        <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 3, bgcolor: 'white', minHeight: 280, width: '100%', display: 'flex', flexDirection: 'column' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle2" fontWeight="600">Employee Transitions</Typography>
                  <SwapHoriz sx={{ color: '#2196f3' }} />
              </Stack>
              <Box sx={{ overflowY: 'auto', flex: 1 }}>
                  {transitions.length > 0 ? transitions.map((trans, idx) => (
                      <Box key={idx} sx={{ p: 1.5, mb: 1, bgcolor: '#f8f9fa', borderRadius: 2, borderLeft: '3px solid #2196f3' }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                              <Typography variant="body2" fontWeight="600">{trans.transitionType || 'Transition'}</Typography>
                              <Chip label={trans.status || 'Pending'} size="small" sx={{ height: 18, fontSize: 9 }} color={trans.status === 'APPROVED' ? 'success' : 'warning'} />
                          </Stack>
                          <Typography variant="caption" color="text.secondary">{trans.fromDepartment} → {trans.toDepartment}</Typography>
                          <Typography variant="caption" display="block" color="text.secondary">{trans.effectiveDate ? new Date(trans.effectiveDate).toLocaleDateString() : ''}</Typography>
                      </Box>
                  )) : (
                      <Box textAlign="center" py={4} sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                          <SwapHoriz sx={{ fontSize: 40, color: '#e0e0e0', mb: 1 }} />
                          <Typography variant="caption" color="text.secondary" display="block">No transitions</Typography>
                      </Box>
                  )}
              </Box>
          </Paper>
        </Grid>

        {/* Document Expiry */}
        <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 3, bgcolor: 'white', minHeight: 280, width: '100%', display: 'flex', flexDirection: 'column' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle2" fontWeight="600">Documents Expiring Soon</Typography>
                  <Warning sx={{ color: '#f44336' }} />
              </Stack>
              <Box sx={{ overflowY: 'auto', flex: 1 }}>
                  {expiringDocs.length > 0 ? expiringDocs.map((doc, idx) => (
                      <Stack key={idx} direction="row" spacing={2} alignItems="center" sx={{ p: 1.5, mb: 1, bgcolor: '#fff3e0', borderRadius: 2 }}>
                          <Description sx={{ color: '#ff9800' }} />
                          <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" fontWeight="500">{doc.documentName || doc.documentType}</Typography>
                              <Typography variant="caption" color="error">Expires: {doc.expiryDate ? new Date(doc.expiryDate).toLocaleDateString() : 'N/A'}</Typography>
                          </Box>
                      </Stack>
                  )) : (
                      <Box textAlign="center" py={4} sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                          <CheckCircle sx={{ fontSize: 40, color: '#4caf50', mb: 1 }} />
                          <Typography variant="caption" color="text.secondary" display="block">All documents valid</Typography>
                      </Box>
                  )}
              </Box>
          </Paper>
        </Grid>

        {/* Assets (Moved to Row 2) */}
        <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 3, bgcolor: 'white', minHeight: 280, width: '100%', display: 'flex', flexDirection: 'column' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle2" fontWeight="600">Assigned Assets</Typography>
                  <Computer sx={{ color: '#607d8b' }} />
              </Stack>
              <Box sx={{ flex: 1, overflowY: 'auto' }}>
                  {assets.length > 0 ? assets.slice(0, 4).map((asset, idx) => (
                      <Stack key={idx} direction="row" spacing={1.5} alignItems="center" sx={{ p: 1.5, mb: 1, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                          <Box sx={{ width: 36, height: 36, bgcolor: '#1a1d1f', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              <Computer sx={{ color: 'white', fontSize: 18 }} />
                          </Box>
                          <Box sx={{ overflow: 'hidden' }}>
                              <Typography variant="body2" fontWeight="500" noWrap>{asset.assetName}</Typography>
                              <Typography variant="caption" color="text.secondary" noWrap display="block">{asset.assetCode}</Typography>
                          </Box>
                      </Stack>
                  )) : (
                      <Box textAlign="center" py={4} sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                          <Computer sx={{ fontSize: 40, color: '#e0e0e0', mb: 1 }} />
                          <Typography variant="caption" color="text.secondary">No assets assigned</Typography>
                      </Box>
                  )}
              </Box>
          </Paper>
        </Grid>

        {/* Row 3 - Tasks (Expanded) + Performance */}
        {/* Tasks List (Expanded) */}
        <Grid item xs={12} md={8} sx={{ display: 'flex' }}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 3, bgcolor: '#1a1d1f', color: 'white', minHeight: 300, width: '100%', display: 'flex', flexDirection: 'column' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                  <Box>
                      <Typography variant="subtitle2" fontWeight="600" fontSize={16}>Scheduled Tasks</Typography>
                      <Typography variant="caption" sx={{ opacity: 0.6 }}>{tasks.length} Active Tasks</Typography>
                  </Box>
                  <Button variant="contained" size="small" startIcon={<AssignmentOutlined />} sx={{ bgcolor: 'rgba(255,255,255,0.1)', textTransform: 'none' }}>View All</Button>
              </Stack>
              <Box sx={{ overflowY: 'auto', flex: 1 }}>
                  <Grid container spacing={2}>
                      {tasks.slice(0, 6).map((task, idx) => {
                          const taskDate = task.dueDate ? new Date(task.dueDate) : new Date();
                          const day = taskDate.getDate();
                          const month = taskDate.toLocaleString('default', { month: 'short' });
                          
                          return (
                              <Grid item xs={12} md={6} key={task._id}>
                                  <Stack direction="row" spacing={2} alignItems="center" sx={{ p: 1.5, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 3, transition: '0.2s', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
                                      {/* Date Calendar Box */}
                                      <Box sx={{ 
                                          minWidth: 50, height: 50, borderRadius: 2, 
                                          bgcolor: 'rgba(255,255,255,0.1)', 
                                          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                          border: '1px solid rgba(255,255,255,0.1)'
                                      }}>
                                          <Typography variant="caption" sx={{ fontSize: 10, opacity: 0.7, textTransform: 'uppercase' }}>{month}</Typography>
                                          <Typography variant="h6" fontWeight="700" lineHeight={1}>{day}</Typography>
                                      </Box>
                                      
                                      {/* Content */}
                                      <Box sx={{ flex: 1, overflow: 'hidden' }}>
                                          <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                                              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: task.priority === 'HIGH' ? '#f44336' : task.priority === 'MEDIUM' ? '#ff9800' : '#4caf50' }} />
                                              <Typography variant="caption" sx={{ color: task.priority === 'HIGH' ? '#f44336' : task.priority === 'MEDIUM' ? '#ff9800' : '#4caf50', fontWeight: 600, fontSize: 10 }}>{task.priority || 'NORMAL'}</Typography>
                                          </Stack>
                                          <Typography variant="body2" fontWeight="600" noWrap title={task.title}>{task.title}</Typography>
                                          <Typography variant="caption" sx={{ opacity: 0.5 }} noWrap>{task.description || 'No description provided'}</Typography>
                                      </Box>

                                      {/* Avatar/Status */}
                                      <Stack alignItems="center" spacing={0.5}>
                                          <Avatar src={getImageUrl(selectedEmployee.imageUrl)} sx={{ width: 28, height: 28, border: '1px solid rgba(255,255,255,0.2)' }}>{selectedEmployee.staff_name?.[0]}</Avatar>
                                          {task.status === 'COMPLETED' ? 
                                              <CheckCircle sx={{ color: '#4caf50', fontSize: 16 }} /> : 
                                              <RadioButtonUnchecked sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 16 }} />
                                          }
                                      </Stack>
                                  </Stack>
                              </Grid>
                          );
                      })}
                      {tasks.length === 0 && (
                          <Grid item xs={12}>
                              <Box textAlign="center" py={6} sx={{ opacity: 0.5 }}>
                                  <EventBusy sx={{ fontSize: 40, mb: 1 }} />
                                  <Typography>No scheduled tasks</Typography>
                              </Box>
                          </Grid>
                      )}
                  </Grid>
              </Box>
          </Paper>
        </Grid>

        {/* Performance */}
        <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 3, bgcolor: 'white', minHeight: 300, width: '100%', display: 'flex', flexDirection: 'column' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                  <Box>
                      <Typography variant="subtitle2" fontWeight="600">Performance</Typography>
                      <Typography variant="caption" color="text.secondary">{performanceTab === 0 ? 'General Metrics' : 'POS Sales Analysis'}</Typography>
                  </Box>
                  <Stack direction="row" spacing={1}>
                      <IconButton size="small" onClick={() => setPerformanceTab(prev => prev === 0 ? 1 : 0)} disabled={performanceTab === 0}>
                          <ChevronLeft fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => setPerformanceTab(prev => prev === 0 ? 1 : 0)} disabled={performanceTab === 1}>
                          <ChevronRight fontSize="small" />
                      </IconButton>
                  </Stack>
              </Stack>
              
              <Box sx={{ flex: 1 }}>
                  {performanceTab === 0 ? (
                      // General Performance View
                      (() => {
                          const completedTasks = tasks.filter(t => t.status === 'COMPLETED').length;
                          const totalTasks = tasks.length;
                          const taskScore = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
                          const attTotal = attendance.present + attendance.absent + attendance.late;
                          const attScore = attTotal > 0 ? Math.round((attendance.present / attTotal) * 100) : 0;
                          const kpiScore = totalTasks > 0 || attTotal > 0 ? Math.round((taskScore + attScore) / (totalTasks > 0 && attTotal > 0 ? 2 : 1)) : 0;
                          const isTopPerformer = kpiScore >= 80;
                          const isImproving = kpiScore >= 50 && kpiScore < 80;
                          const starRating = kpiScore >= 90 ? 5 : kpiScore >= 70 ? 4 : kpiScore >= 50 ? 3 : kpiScore >= 30 ? 2 : 1;
                          return (
                      <>
                          <Grid container spacing={2} alignItems="center" mb={2}>
                              <Grid item xs={6}>
                                  <Box sx={{ textAlign: 'center' }}>
                                      <Box sx={{ width: 90, height: 90, borderRadius: '50%', border: '8px solid #e0e0e0', borderTopColor: kpiScore >= 50 ? '#4caf50' : '#ff9800', borderRightColor: kpiScore >= 50 ? '#4caf50' : '#ff9800', mx: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                          <Typography variant="h4" fontWeight="700">{kpiScore}%</Typography>
                                      </Box>
                                      <Typography variant="caption" color="text.secondary" mt={1} display="block">KPI Score</Typography>
                                  </Box>
                              </Grid>
                              <Grid item xs={6}>
                                  <Stack spacing={1}>
                                      {isTopPerformer && <Chip icon={<EmojiEvents sx={{ fontSize: '16px !important' }} />} label="Top Performer" size="small" color="warning" variant="outlined" sx={{ justifyContent: 'flex-start', pl: 1 }} />}
                                      {isImproving && <Chip icon={<TrendingUp sx={{ fontSize: '16px !important' }} />} label="Improving" size="small" color="success" variant="outlined" sx={{ justifyContent: 'flex-start', pl: 1 }} />}
                                      {!isTopPerformer && !isImproving && <Chip icon={<TrendingDown sx={{ fontSize: '16px !important' }} />} label="Needs Improvement" size="small" color="error" variant="outlined" sx={{ justifyContent: 'flex-start', pl: 1 }} />}
                                      <Chip icon={<Star sx={{ fontSize: '16px !important' }} />} label={`${starRating} Star Rating`} size="small" color="primary" variant="outlined" sx={{ justifyContent: 'flex-start', pl: 1 }} />
                                  </Stack>
                              </Grid>
                          </Grid>

                          <Divider sx={{ my: 2 }} />

                          <Box>
                              <Typography variant="caption" color="text.secondary" fontWeight="600" textTransform="uppercase" mb={1} display="block">Performance Summary</Typography>
                              <Stack spacing={1.5}>
                                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 1.5, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                                      <Typography variant="caption" color="text.secondary">Tasks Completed</Typography>
                                      <Typography variant="body2" fontWeight="600">{completedTasks}/{totalTasks}</Typography>
                                  </Stack>
                                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 1.5, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                                      <Typography variant="caption" color="text.secondary">Attendance Rate</Typography>
                                      <Typography variant="body2" fontWeight="600">{attScore}%</Typography>
                                  </Stack>
                                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 1.5, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                                      <Typography variant="caption" color="text.secondary">Late Arrivals</Typography>
                                      <Typography variant="body2" fontWeight="600" color={attendance.late > 0 ? '#ff9800' : '#4caf50'}>{attendance.late}</Typography>
                                  </Stack>
                              </Stack>
                          </Box>
                      </>
                          );
                      })()
                  ) : (
                      // Leave & Attendance Summary View
                      <>
                          <Box>
                              <Typography variant="caption" color="text.secondary" fontWeight="600" textTransform="uppercase" mb={1} display="block">Attendance Breakdown</Typography>
                              <Stack spacing={1.5}>
                                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 1.5, bgcolor: '#e8f5e9', borderRadius: 2 }}>
                                      <Stack direction="row" spacing={1} alignItems="center">
                                          <CheckCircle sx={{ fontSize: 16, color: '#4caf50' }} />
                                          <Typography variant="body2">Present Days</Typography>
                                      </Stack>
                                      <Typography variant="body2" fontWeight="700" color="#4caf50">{attendance.present}</Typography>
                                  </Stack>
                                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 1.5, bgcolor: '#ffebee', borderRadius: 2 }}>
                                      <Stack direction="row" spacing={1} alignItems="center">
                                          <EventBusy sx={{ fontSize: 16, color: '#f44336' }} />
                                          <Typography variant="body2">Absent Days</Typography>
                                      </Stack>
                                      <Typography variant="body2" fontWeight="700" color="#f44336">{attendance.absent}</Typography>
                                  </Stack>
                                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 1.5, bgcolor: '#fff3e0', borderRadius: 2 }}>
                                      <Stack direction="row" spacing={1} alignItems="center">
                                          <AccessTime sx={{ fontSize: 16, color: '#ff9800' }} />
                                          <Typography variant="body2">Late Arrivals</Typography>
                                      </Stack>
                                      <Typography variant="body2" fontWeight="700" color="#ff9800">{attendance.late}</Typography>
                                  </Stack>
                              </Stack>

                              <Divider sx={{ my: 2 }} />

                              <Typography variant="caption" color="text.secondary" fontWeight="600" textTransform="uppercase" mb={1} display="block">Leave Summary</Typography>
                              <Grid container spacing={1}>
                                  <Grid item xs={4}>
                                      <Box sx={{ p: 1, bgcolor: '#f5f5f5', borderRadius: 1, textAlign: 'center' }}>
                                          <Typography variant="caption" display="block" color="text.secondary">Total</Typography>
                                          <Typography variant="body2" fontWeight="700">{leaves.length}</Typography>
                                      </Box>
                                  </Grid>
                                  <Grid item xs={4}>
                                      <Box sx={{ p: 1, bgcolor: '#f5f5f5', borderRadius: 1, textAlign: 'center' }}>
                                          <Typography variant="caption" display="block" color="text.secondary">Approved</Typography>
                                          <Typography variant="body2" fontWeight="700" color="#4caf50">{leaves.filter(l => l.status === 'Approved').length}</Typography>
                                      </Box>
                                  </Grid>
                                  <Grid item xs={4}>
                                      <Box sx={{ p: 1, bgcolor: '#f5f5f5', borderRadius: 1, textAlign: 'center' }}>
                                          <Typography variant="caption" display="block" color="text.secondary">Pending</Typography>
                                          <Typography variant="body2" fontWeight="700" color="#ff9800">{leaves.filter(l => l.status === 'Pending' || !l.status).length}</Typography>
                                      </Box>
                                  </Grid>
                              </Grid>
                          </Box>
                      </>
                  )}
              </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

const AvatarGroupMock = () => (
    <Stack direction="row" spacing={-1} mt={0.5}>
        {[1,2,3].map(i => (
            <Avatar key={i} sx={{ width: 20, height: 20, border: '2px solid white', fontSize: 10 }}>U{i}</Avatar>
        ))}
    </Stack>
)

export default HRMDashboard;
